import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Heart, MessageCircle, Share2, Music, Clapperboard, ChevronUp, ChevronDown, Eye, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { storiesApi, ForYouItem } from '@/api/stories';
import { apiClient } from '@/api/client';
import Avatar from '@/components/ui/Avatar';
import FollowButton from '@/components/ui/FollowButton';
import { useAuthStore } from '@/store/authStore';
import SoundPage from './SoundPage';
import StoryCreateModal from './StoryCreateModal';

/**
 * Вертикальная лента "For You" в стиле TikTok: одна сториз во весь экран,
 * свайп вверх → следующая. Счёт просмотра делается при входе карточки в viewport.
 */
export default function ForYouFeed({ onOpenTag }: { onOpenTag?: (tag: string) => void }) {
  const qc = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const showAdult = useAuthStore((s) => s.user?.showAdult);
  // Кэш "per-story reveal" для SUGGESTIVE — сняли размытие на конкретной сторис.
  const [softRevealed, setSoftRevealed] = useState<Set<string>>(new Set());

  const confirmAdult = async () => {
    try {
      await apiClient.post('/api/users/adult-consent', { show: true });
      useAuthStore.setState((s) => ({
        user: s.user ? { ...s.user, showAdult: true, ageVerifiedAt: new Date().toISOString() } : s.user,
      }));
    } catch { /* ignore */ }
  };
  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['stories', 'for-you'],
    queryFn: ({ pageParam }) => storiesApi.getForYou(pageParam as string | undefined, 10),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = useMemo<ForYouItem[]>(
    () => (data?.pages ?? []).flatMap((p) => p.items),
    [data],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const viewedRef = useRef<Set<string>>(new Set());
  const [soundTrackId, setSoundTrackId] = useState<string | null>(null);
  const [duetFor, setDuetFor] = useState<{ parentId: string; mode: 'DUET' | 'STITCH' } | null>(null);

  // Observe which card is centered → record view + preload next page.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= 0.6) {
            const id = (entry.target as HTMLElement).dataset.storyId!;
            setActiveId(id);
            if (!viewedRef.current.has(id)) {
              viewedRef.current.add(id);
              storiesApi.markViewed(id).catch(() => {/* silent */});
            }
          }
        }
      },
      { root, threshold: [0.6] },
    );
    root.querySelectorAll<HTMLElement>('[data-story-id]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [items.length]);

  // Navigation helper — прокручивает контейнер ровно на одну карточку вверх/вниз.
  // Каждая сторис занимает всю высоту (h-full + snap-mandatory), поэтому
  // делаем scroll by clientHeight; smooth behavior триггерит snap анимацию.
  const scrollByPage = (direction: 1 | -1) => {
    const root = containerRef.current;
    if (!root) return;
    root.scrollBy({ top: direction * root.clientHeight, behavior: 'smooth' });
  };

  // Keyboard — ↑/↓/PageUp/PageDown/Space/Home/End для навигации без мышки.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Игнорируем если пользователь печатает в input/textarea.
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault(); scrollByPage(1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault(); scrollByPage(-1);
      } else if (e.key === ' ') {
        e.preventDefault(); scrollByPage(1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Infinite scroll trigger
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const sentinel = root.querySelector<HTMLElement>('[data-sentinel]');
    if (!sentinel) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage) fetchNextPage();
      },
      { root, threshold: 0.1 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasNextPage, fetchNextPage, items.length]);

  const likeMut = useMutation({
    mutationFn: (id: string) => storiesApi.toggleLike(id),
    onSuccess: (res, id) => {
      qc.setQueryData<{ pages: { items: ForYouItem[] }[] } | undefined>(
        ['stories', 'for-you'],
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            pages: prev.pages.map((p) => ({
              ...p,
              items: p.items.map((it) =>
                it.id === id ? { ...it, isLiked: res.liked, likeCount: res.likeCount } : it,
              ),
            })),
          };
        },
      );
    },
  });

  const shareMut = useMutation({
    mutationFn: (id: string) => storiesApi.recordShare(id),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center h-full text-surface-400">Загрузка…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-surface-400 px-6 text-center">
        Пока нет рекомендаций — поставьте лайки нескольким sторисам, чтобы лента настроилась.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full w-full overflow-y-auto snap-y snap-mandatory scrollbar-none bg-black"
    >
      {items.map((it) => {
        const isAdult = it.contentRating === 'ADULT';
        const isSuggestive = it.contentRating === 'SUGGESTIVE';
        const needsAgeGate = isAdult && !showAdult;
        const softBlur = isSuggestive && !softRevealed.has(it.id);
        const mediaBlurClass = needsAgeGate || softBlur ? 'blur-2xl scale-110' : '';
        return (
        <div
          key={it.id}
          data-story-id={it.id}
          className="h-full w-full snap-start relative flex items-center justify-center"
        >
          {it.mimeType.startsWith('video/') ? (
            <video
              src={it.mediaUrl}
              poster={it.thumbnailUrl}
              className={clsx('max-h-full max-w-full object-contain transition-[filter] duration-200', mediaBlurClass)}
              autoPlay={activeId === it.id && !needsAgeGate}
              loop
              playsInline
              muted={activeId !== it.id}
              controls={false}
            />
          ) : (
            <img
              src={it.mediaUrl}
              alt=""
              className={clsx('max-h-full max-w-full object-contain transition-[filter] duration-200', mediaBlurClass)}
            />
          )}

          {/* Hard 18+ gate для ADULT */}
          {needsAgeGate && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 p-6 text-center">
              <div className="max-w-xs space-y-4 text-white">
                <div className="w-16 h-16 mx-auto rounded-full bg-red-600/20 flex items-center justify-center">
                  <AlertTriangle size={32} className="text-red-400" />
                </div>
                <h3 className="text-lg font-bold">Контент 18+</h3>
                <p className="text-sm text-surface-300">
                  В этой сторис деликатное содержимое. Подтверди что тебе 18 и более.
                </p>
                <button
                  onClick={confirmAdult}
                  className="w-full py-2 bg-red-600 hover:bg-red-500 rounded-lg font-medium"
                >
                  Мне 18+ · показать
                </button>
                <p className="text-[10px] text-surface-500">
                  Согласие запомнится для всех ADULT-сторис в этой сессии.
                </p>
              </div>
            </div>
          )}

          {/* Soft blur для SUGGESTIVE — tap to reveal */}
          {softBlur && !needsAgeGate && (
            <button
              onClick={() => setSoftRevealed((s) => new Set(s).add(it.id))}
              className="absolute inset-0 z-10 flex items-center justify-center"
            >
              <div className="bg-black/40 backdrop-blur-sm text-white px-5 py-3 rounded-full flex items-center gap-2">
                <Eye size={16} /> Показать
              </div>
            </button>
          )}

          {/* Overlay: author + caption + actions */}
          <div className="absolute inset-x-0 bottom-0 p-4 pb-10 bg-gradient-to-t from-black/80 to-transparent text-white">
            <div className="flex items-center gap-2 mb-2">
              <Avatar src={it.author.avatarUrl} username={it.author.displayName} size="md" />
              <div className="font-semibold">{it.author.displayName}</div>
              {currentUserId && it.author.id !== currentUserId && (
                <FollowButton userId={it.author.id} className="ml-auto" />
              )}
            </div>
            {it.caption && (
              <div className="text-sm mb-2 whitespace-pre-wrap">
                {renderCaption(it.caption, onOpenTag)}
              </div>
            )}
            {it.musicTrack && (
              <button
                onClick={() => setSoundTrackId(it.musicTrack!.id)}
                className="flex items-center gap-2 text-xs text-surface-200 hover:text-white max-w-full"
              >
                <Music size={14} />
                <span className="truncate">
                  {it.musicTrack.artist} — {it.musicTrack.title}
                </span>
              </button>
            )}
          </div>

          {/* Right-side action rail */}
          <div className="absolute right-2 bottom-24 flex flex-col items-center gap-4 text-white">
            <button
              onClick={() => likeMut.mutate(it.id)}
              className="flex flex-col items-center gap-1"
            >
              <div className={clsx(
                'w-11 h-11 rounded-full bg-surface-900/60 backdrop-blur flex items-center justify-center',
                it.isLiked && 'text-red-500',
              )}>
                <Heart size={22} fill={it.isLiked ? 'currentColor' : 'none'} />
              </div>
              <span className="text-xs">{it.likeCount}</span>
            </button>
            <button className="flex flex-col items-center gap-1">
              <div className="w-11 h-11 rounded-full bg-surface-900/60 backdrop-blur flex items-center justify-center">
                <MessageCircle size={22} />
              </div>
              <span className="text-xs">{it.commentCount}</span>
            </button>
            <button
              onClick={() => shareMut.mutate(it.id)}
              className="flex flex-col items-center gap-1"
            >
              <div className="w-11 h-11 rounded-full bg-surface-900/60 backdrop-blur flex items-center justify-center">
                <Share2 size={22} />
              </div>
              <span className="text-xs">{it.shareCount}</span>
            </button>
            <button
              onClick={() => setDuetFor({ parentId: it.id, mode: 'DUET' })}
              className="flex flex-col items-center gap-1"
              title="Снять ответ (Duet)"
            >
              <div className="w-11 h-11 rounded-full bg-surface-900/60 backdrop-blur flex items-center justify-center">
                <Clapperboard size={22} />
              </div>
              <span className="text-xs">Duet</span>
            </button>
          </div>
        </div>
        );
      })}
      <div data-sentinel className="h-8" />

      {/* Nav arrows — видны только на desktop (mobile использует свайпы) */}
      <button
        onClick={() => scrollByPage(-1)}
        className="hidden md:flex fixed right-4 bottom-24 z-30 w-12 h-12 rounded-full bg-surface-900/70 backdrop-blur text-white items-center justify-center hover:bg-surface-800 transition"
        title="Предыдущая (↑)"
      >
        <ChevronUp size={24} />
      </button>
      <button
        onClick={() => scrollByPage(1)}
        className="hidden md:flex fixed right-4 bottom-8 z-30 w-12 h-12 rounded-full bg-surface-900/70 backdrop-blur text-white items-center justify-center hover:bg-surface-800 transition"
        title="Следующая (↓)"
      >
        <ChevronDown size={24} />
      </button>

      {soundTrackId && (
        <SoundPage trackId={soundTrackId} onClose={() => setSoundTrackId(null)} />
      )}

      {duetFor && (
        <StoryCreateModal
          parentStoryId={duetFor.parentId}
          replyMode={duetFor.mode}
          onClose={() => setDuetFor(null)}
          onCreated={() => {
            setDuetFor(null);
            qc.invalidateQueries({ queryKey: ['stories', 'for-you'] });
          }}
        />
      )}
    </div>
  );
}

function renderCaption(caption: string, onOpenTag?: (tag: string) => void) {
  // Linkify hashtags — `#foo` is rendered as a clickable span.
  const parts = caption.split(/(\s+)/);
  return parts.map((chunk, i) => {
    const m = chunk.match(/^#([\p{L}\p{N}_]{1,64})$/u);
    if (m) {
      const tag = m[1].toLocaleLowerCase('ru-RU');
      return (
        <button
          key={i}
          className="text-brand-400 hover:underline"
          onClick={() => onOpenTag?.(tag)}
        >
          {chunk}
        </button>
      );
    }
    return <span key={i}>{chunk}</span>;
  });
}
